import { Mutation, JsonApiRequestError } from '@farfetched/core';
import { chain } from '@leprecoins/effector';
import { sample, Store } from 'effector';
import {
	DepositWalletMutationParams,
	UuidResponse,
	WalletWithAccountId,
	createDepositWalletMutation,
} from 'shared/api';
import { createModalFormModel, ModalFormModel, notEmptyRule, notify } from 'shared/lib';

export type DepositWalletFormValue = {
	amount: string;
};

export type DepositWalletModel = ModalFormModel<WalletWithAccountId, DepositWalletFormValue> & {
	depositWalletMutation: Mutation<DepositWalletMutationParams, UuidResponse, JsonApiRequestError>;
	$pending: Store<boolean>;
};

export const createDepositWalletModel = (): DepositWalletModel => {
	const depositWalletMutation = createDepositWalletMutation();

	const model = createModalFormModel<WalletWithAccountId, DepositWalletFormValue>({
		fields: {
			amount: {
				init: '',
				rules: [notEmptyRule],
			},
		},
		resetErrorsOnChange: true,
		validateOn: ['submit'],
	});

	const { $pending } = depositWalletMutation;

	sample({
		clock: model.form.submitted,
		source: {
			amount: model.form.fields.amount.$value,
			wallet: model.$modalData,
		},
		filter: ({ wallet }) => wallet !== null,
		fn: ({ amount, wallet }) => ({
			amount: Number(amount),
			accountId: wallet!.accountId,
			currency: wallet!.currency,
		}),
		target: depositWalletMutation.start,
	});

	chain(depositWalletMutation.finished.success, model.switcher.disable);

	notify(depositWalletMutation.finished.success, 'deposit-wallet.success');

	chain(model.modalClosed, depositWalletMutation.reset);

	return {
		depositWalletMutation,
		$pending,
		...model,
	};
};
